import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Paper,
  Text,
  Button,
  Group,
  Stack,
  Badge,
  Loader,
  Alert,
  Box,
  Center,
  Progress,
} from "@mantine/core";
import { IconTrophy } from "@tabler/icons-react";

// Incentives Section Component
// Default: full-size grid of cards. Click a card → split-panel (grid left, detail right).
function IncentivesTab() {
  const { getVerifiedJWT } = useAuth();
  const [incentives, setIncentives] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIncentive, setSelectedIncentive] = useState(null);

  const fetchIncentives = async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Failed to authenticate");
        setLoading(false);
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/incentives/my-incentives"),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setIncentives(result.data);
        } else {
          setIncentives([]);
        }
      } else {
        setError("Failed to load incentives");
      }
    } catch (err) {
      setError(err.message || "Failed to load incentives");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncentives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Stack align="center" spacing="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading incentives...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        <Text>{error}</Text>
        <Button size="sm" mt="md" onClick={fetchIncentives}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!incentives || incentives.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center" spacing="md">
          <IconTrophy size={52} color="gray" />
          <Text size="lg" fw={600}>
            No Incentives Yet
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Incentives assigned to you by your manager will appear here.
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Split-panel view — shown after a card is clicked
  if (selectedIncentive) {
    return (
      <div className="incentives-split-layout">
        {/* Left panel — original grid cards, same size as before */}
        <div className="incentives-left-panel">
          <div className="incentives-left-panel-grid">
            {incentives.map(incentive => (
              <IncentiveGridCard
                key={incentive.id}
                incentive={incentive}
                isSelected={selectedIncentive?.id === incentive.id}
                onClick={() => setSelectedIncentive(incentive)}
              />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="incentives-split-divider" />

        {/* Right panel — detail view */}
        <div className="incentives-right-panel">
          <IncentiveDetailView incentive={selectedIncentive} />
        </div>
      </div>
    );
  }

  // Default grid view — no card selected yet
  return (
    <div className="incentives-grid">
      {incentives.map(incentive => (
        <IncentiveGridCard
          key={incentive.id}
          incentive={incentive}
          onClick={() => setSelectedIncentive(incentive)}
        />
      ))}
    </div>
  );
}

// Milestone Timeline Component
function MilestoneTimeline({ incentive }) {
  const currentValue = incentive.current_value ?? 0;
  const [now, setNow] = useState(Date.now());

  // Tick every second for live countdown
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const competitors = incentive.competitors || [];
  const heroImage =
    incentive.banner_url ||
    incentive.image_url ||
    incentive.prizes?.find(p => p?.image_url)?.image_url ||
    null;

  // Sort prizes ascending by benchmark so index 0 = lowest, last = highest
  const prizesAsc = [...(incentive.prizes || [])].sort(
    (a, b) => (a.benchmark_value || 0) - (b.benchmark_value || 0)
  );

  const formatValue = val => {
    if (val === null || val === undefined) return "0";
    if (val >= 1000)
      return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
    return val.toString();
  };

  const formatOverlayValue = val => {
    if (val === null || val === undefined) return "0";
    const rounded = Number(val.toFixed(2));
    return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
  };

  const getIncentiveMetricLabel = metricType => {
    const labels = {
      training_minutes: "Minutes",
      days_completed: "Days",
      videos_completed: "Videos",
      leads: "Leads",
      first_meetings: "1st Meetings",
      additional_meetings: "Additional Meetings",
      offers: "Offers",
      signed: "Signed",
      total_score: "Score",
    };

    return labels[metricType] || "Metric";
  };

  const getSegmentOverlays = (segmentStart, segmentEnd) => {
    const start = Number(segmentStart || 0);
    const end = Number(segmentEnd || 0);
    const delta = end - start;

    if (delta <= 0) return [];

    return [0.25, 0.5, 0.75].map(ratio => ({
      value: Math.ceil(start + delta * ratio),
      topPercent: ratio * 100,
    }));
  };

  const formatDate = dateStr => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const isAchieved = prize => currentValue >= (prize.benchmark_value || 0);

  // eslint-disable-next-line no-unused-vars
  const getNodeProgress = (prize, prizeIndexInAsc) => {
    const target = prize.benchmark_value || 0;
    if (currentValue >= target) return 1;
    const prevTarget =
      prizeIndexInAsc > 0
        ? prizesAsc[prizeIndexInAsc - 1].benchmark_value || 0
        : 0;
    if (target <= prevTarget) return 1;
    return Math.min(
      1,
      Math.max(0, (currentValue - prevTarget) / (target - prevTarget))
    );
  };

  const getCompetitorsInSegment = (segStart, segEnd, isLastSegment = false) => {
    const start = Number(segStart || 0);
    const end = Number(segEnd || 0);
    const delta = end - start;
    if (!competitors || competitors.length === 0) return [];

    return competitors
      .filter(c => {
        const v = Number(c.current_value || 0);
        return v >= start && (v < end || isLastSegment);
      })
      .map((c, idx) => {
        const v = Number(c.current_value || 0);
        const topPercent =
          delta > 0
            ? Math.min(100, Math.max(0, ((v - start) / delta) * 100))
            : 50;
        return { competitor: c, topPercent, idx };
      });
  };

  const getSegmentAboveFill = ascIndex => {
    if (ascIndex === 0) return 0;
    const fromBenchmark = prizesAsc[ascIndex - 1].benchmark_value || 0;
    const toBenchmark = prizesAsc[ascIndex].benchmark_value || 0;
    if (toBenchmark <= fromBenchmark) return 100;
    const fill =
      ((currentValue - fromBenchmark) / (toBenchmark - fromBenchmark)) * 100;
    return Math.min(100, Math.max(0, fill));
  };

  const getStartingSegmentFill = () => {
    if (prizesAsc.length === 0) return 0;
    const firstTarget = prizesAsc[0].benchmark_value || 0;
    if (firstTarget <= 0) return 100;
    const fill = (currentValue / firstTarget) * 100;
    return Math.min(100, Math.max(0, fill));
  };

  const startingFill = getStartingSegmentFill();

  const unlockedCount = prizesAsc.filter(
    p => currentValue >= (p.benchmark_value || 0)
  ).length;
  const remainingCount = prizesAsc.filter(
    p => currentValue < (p.benchmark_value || 0)
  ).length;
  const totalGoal =
    prizesAsc.length > 0
      ? prizesAsc[prizesAsc.length - 1].benchmark_value || 0
      : 0;
  const remainingToWinAll = Math.max(0, totalGoal - currentValue);
  const metricLabel = getIncentiveMetricLabel(incentive.metric_type);

  const hasLimit = prizesAsc.some(
    p => p.first_to_limit != null && p.first_to_limit > 0
  );

  const getPrizeEarnedCount = prize => {
    if (!prize.benchmark_value) return 0;
    return competitors.filter(
      c => (c.current_value || 0) >= (prize.benchmark_value || 0)
    ).length;
  };

  // eslint-disable-next-line no-unused-vars
  const totalRemainingPrizeSlots = hasLimit
    ? prizesAsc.reduce((sum, p) => {
        if (p.first_to_limit == null || p.first_to_limit <= 0) return sum;
        const earned = getPrizeEarnedCount(p);
        return sum + Math.max(0, p.first_to_limit - earned);
      }, 0)
    : null;

  // eslint-disable-next-line no-unused-vars
  const totalParticipants = competitors.length;

  const nextPrize = prizesAsc.find(
    p => currentValue < (p.benchmark_value || 0)
  );
  const nextPrizeProgress = nextPrize
    ? (() => {
        const target = nextPrize.benchmark_value || 0;
        const idx = prizesAsc.indexOf(nextPrize);
        const prevTarget =
          idx > 0 ? prizesAsc[idx - 1].benchmark_value || 0 : 0;
        if (target <= prevTarget) return 100;
        return Math.min(
          100,
          Math.max(
            0,
            ((currentValue - prevTarget) / (target - prevTarget)) * 100
          )
        );
      })()
    : 100;
  const nextPrizeAmountLeft = nextPrize
    ? Math.max(0, (nextPrize.benchmark_value || 0) - currentValue)
    : 0;

  const getTimeLeftParts = () => {
    if (!incentive.end_date) return null;
    const end = new Date(incentive.end_date);
    const diffMs = end.getTime() - now;
    if (diffMs <= 0) return null;
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const timeLeftParts = getTimeLeftParts();

  return (
    <Paper
      p="xl"
      withBorder
      className="milestone-timeline-card"
      style={{ marginTop: -40 }}
    >
      {/* Incentive title at top */}
      <div className="milestone-timeline-header">
        <span className="milestone-timeline-title">{incentive.title}</span>
      </div>

      {/* Incentive image under title */}
      {heroImage && (
        <div className="milestone-hero-image-wrap">
          <img
            src={heroImage}
            alt={incentive.title}
            className="milestone-hero-image"
          />
        </div>
      )}

      {/* Next Reward Banner */}
      {nextPrize && (
        <div className="milestone-next-reward-row">
          <div className="milestone-next-reward-banner">
            <div className="milestone-next-reward-icon">
              {nextPrize.image_url ? (
                <img
                  src={nextPrize.image_url}
                  alt={nextPrize.title}
                  className="milestone-next-reward-img"
                />
              ) : (
                <span className="milestone-next-reward-emoji">🏆</span>
              )}
            </div>
            <div className="milestone-next-reward-info">
              <span className="milestone-next-reward-label">Next Reward</span>
              <span className="milestone-next-reward-title">
                {nextPrize.title}
              </span>
              <span className="milestone-next-reward-progress-text">
                {formatValue(currentValue)} /{" "}
                {formatValue(nextPrize.benchmark_value)}
              </span>
              <div className="milestone-next-reward-bar-track">
                <div
                  className="milestone-next-reward-bar-fill"
                  style={{ width: `${nextPrizeProgress}%` }}
                />
              </div>
            </div>
            <div className="milestone-next-reward-remaining">
              <span className="milestone-next-reward-remaining-value">
                {formatValue(nextPrizeAmountLeft)} to go!
              </span>
            </div>
          </div>

          <div className="milestone-total-metric-card">
            <span className="milestone-total-metric-label">
              Total {metricLabel}
            </span>
            <span className="milestone-total-metric-value">
              {formatValue(currentValue)}
            </span>
          </div>
        </div>
      )}

      {/* Summary stats row */}
      <div className="milestone-stats-row">
        <div className="milestone-stat-card">
          <span className="milestone-stat-label">Earned Incentives</span>
          <span className="milestone-stat-value milestone-stat-dark">
            {unlockedCount}
          </span>
        </div>
        <div className="milestone-stat-card">
          <span className="milestone-stat-label">Remaining Incentives</span>
          <span className="milestone-stat-value milestone-stat-dark">
            {remainingCount}
          </span>
        </div>
        <div className="milestone-stat-card">
          <span className="milestone-stat-label">{metricLabel} to Win All</span>
          <span className="milestone-stat-value milestone-stat-purple">
            {formatValue(remainingToWinAll)}
          </span>
        </div>
        <div className="milestone-stat-card">
          <span className="milestone-stat-label">Time Left</span>
          {timeLeftParts ? (
            <div className="milestone-stat-countdown">
              <div className="milestone-stat-countdown-unit">
                <span className="milestone-stat-countdown-num">
                  {timeLeftParts.days}
                </span>
                <span className="milestone-stat-countdown-label">days</span>
              </div>
              <div className="milestone-stat-countdown-unit">
                <span className="milestone-stat-countdown-num">
                  {String(timeLeftParts.hours).padStart(2, "0")}
                </span>
                <span className="milestone-stat-countdown-label">hrs</span>
              </div>
              <div className="milestone-stat-countdown-unit">
                <span className="milestone-stat-countdown-num">
                  {String(timeLeftParts.minutes).padStart(2, "0")}
                </span>
                <span className="milestone-stat-countdown-label">min</span>
              </div>
              <div className="milestone-stat-countdown-unit">
                <span className="milestone-stat-countdown-num">
                  {String(timeLeftParts.seconds).padStart(2, "0")}
                </span>
                <span className="milestone-stat-countdown-label">sec</span>
              </div>
            </div>
          ) : (
            <span className="milestone-stat-value milestone-stat-dark">
              {incentive.end_date ? "Ended" : "No deadline"}
            </span>
          )}
        </div>
      </div>

      {/* Timeline — renders nodes in ascending benchmark order (lowest → highest). */}
      <div className="milestone-timeline-body">
        {prizesAsc.map(prize => {
          const ascIndex = prizesAsc.indexOf(prize);
          const achieved = isAchieved(prize);

          const segAboveFill =
            ascIndex === 0 ? startingFill : getSegmentAboveFill(ascIndex);

          const segAboveOverlays =
            ascIndex === 0
              ? getSegmentOverlays(0, prize.benchmark_value || 0)
              : getSegmentOverlays(
                  prizesAsc[ascIndex - 1].benchmark_value || 0,
                  prize.benchmark_value || 0
                );

          const segStart =
            ascIndex === 0 ? 0 : prizesAsc[ascIndex - 1].benchmark_value || 0;
          const segEnd = prize.benchmark_value || 0;
          const isLastSeg = ascIndex === prizesAsc.length - 1;
          const segCompetitors = getCompetitorsInSegment(
            segStart,
            segEnd,
            isLastSeg
          );

          return (
            <div key={prize.id} className="milestone-node-wrapper">
              {/* Segment above this node (connects from previous node or from start) */}
              <div className="milestone-segment-row">
                <div className="milestone-segment-track">
                  <div
                    className="milestone-segment-fill milestone-segment-fill--top"
                    style={{ height: `${segAboveFill}%` }}
                  />
                  {segAboveOverlays.map((overlay, idx) => (
                    <span
                      key={`overlay-above-${prize.id}-${idx}`}
                      className="milestone-segment-overlay"
                      style={{ top: `${overlay.topPercent}%` }}
                    >
                      {formatOverlayValue(overlay.value)}
                    </span>
                  ))}
                  {/* Competitor avatar pins — fanned out horizontally */}
                  <CompetitorPins
                    competitors={segCompetitors}
                    formatValue={formatValue}
                    prizeId={prize.id}
                  />
                </div>
              </div>

              {/* Prize circle node */}
              <div
                className={`milestone-node ${achieved ? "milestone-node--achieved" : ""}`}
              >
                {prize.image_url ? (
                  <img
                    src={prize.image_url}
                    alt={prize.title}
                    className="milestone-node-image"
                  />
                ) : (
                  <span className="milestone-node-icon">🏆</span>
                )}
                {/* Show earned/limit count badge on the circle — only when first_to_limit is set */}
                {prize.first_to_limit != null && prize.first_to_limit > 0 && (
                  <div className="milestone-node-count-badge">
                    {getPrizeEarnedCount(prize)}/{prize.first_to_limit}
                  </div>
                )}
              </div>

              {/* Label below node */}
              <div className="milestone-node-label">
                <span className="milestone-node-value">
                  {formatValue(currentValue)} /{" "}
                  {formatValue(prize.benchmark_value)}
                </span>
                <span className="milestone-node-name">{prize.title}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Date range at bottom */}
      {(incentive.start_date || incentive.end_date) && (
        <div className="milestone-timeline-dates">
          {formatDate(incentive.start_date)}
          {incentive.start_date && incentive.end_date && " - "}
          {formatDate(incentive.end_date)}
        </div>
      )}
    </Paper>
  );
}

// Incentive Card Component
function IncentiveCard({ incentive }) {
  if (
    incentive.type === "MILESTONE" &&
    incentive.prizes &&
    incentive.prizes.length > 0
  ) {
    return <MilestoneTimeline incentive={incentive} />;
  }

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case "active":
        return "green";
      case "completed":
        return "blue";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const getMetricLabel = metricType => {
    const labels = {
      training_minutes: "Training Minutes",
      days_completed: "Days Completed",
      videos_completed: "Videos Completed",
      leads: "Leads",
      first_meetings: "First Meetings",
      offers: "Offers",
      signed: "Signed",
    };
    return labels[metricType] || metricType;
  };

  const progressPercent =
    incentive.target_value > 0
      ? Math.min((incentive.current_value / incentive.target_value) * 100, 100)
      : 0;

  const daysRemaining = incentive.end_date
    ? Math.max(
        0,
        Math.ceil(
          (new Date(incentive.end_date) - new Date()) / (1000 * 60 * 60 * 24)
        )
      )
    : null;

  return (
    <Paper p="md" withBorder>
      <Stack spacing="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Box style={{ flex: 1 }}>
            <Text fw={600} size="md">
              {incentive.title}
            </Text>
            <Badge size="xs" color="blue" variant="light" mt={4}>
              {getMetricLabel(incentive.metric_type)}
            </Badge>
          </Box>
          <Badge color={getStatusColor(incentive.status)} variant="light">
            {incentive.status}
          </Badge>
        </Group>

        {/* Progress */}
        {incentive.target_value > 0 && (
          <Box>
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">
                Progress
              </Text>
              <Text size="xs" fw={600}>
                {incentive.current_value} / {incentive.target_value}
              </Text>
            </Group>
            <Progress
              value={progressPercent}
              color={incentive.completed ? "green" : "blue"}
            />
          </Box>
        )}

        {/* Stats */}
        <Group grow>
          {incentive.rank && (
            <Box ta="center">
              <Text size="lg" fw={700}>
                #{incentive.rank}
              </Text>
              <Text size="xs" c="dimmed">
                Your Rank
              </Text>
            </Box>
          )}
          {daysRemaining !== null && (
            <Box ta="center">
              <Text size="lg" fw={700}>
                {daysRemaining}
              </Text>
              <Text size="xs" c="dimmed">
                Days Left
              </Text>
            </Box>
          )}
          {incentive.prizes && incentive.prizes.length > 0 && (
            <Box ta="center">
              <Text size="lg" fw={700}>
                {incentive.prizes.length}
              </Text>
              <Text size="xs" c="dimmed">
                Prize{incentive.prizes.length > 1 ? "s" : ""}
              </Text>
            </Box>
          )}
        </Group>

        {/* Description */}
        {incentive.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {incentive.description}
          </Text>
        )}

        {/* Prizes */}
        {incentive.prizes && incentive.prizes.length > 0 && (
          <Box>
            <Text size="xs" fw={600} c="dimmed" mb={4}>
              Prizes
            </Text>
            <Stack spacing={4}>
              {incentive.prizes.slice(0, 3).map((prize, index) => (
                <Group key={prize.id} spacing="xs">
                  <Text size="sm">{["🥇", "🥈", "🥉", "🎁"][index]}</Text>
                  <Text size="sm">{prize.title}</Text>
                </Group>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

// Incentive Grid Card — compact image + title card for the grid view
function IncentiveGridCard({ incentive, isSelected, onClick }) {
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case "active":
        return "#16a34a";
      case "completed":
        return "#2563eb";
      case "cancelled":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  const getMetricLabel = metricType => {
    const labels = {
      training_minutes: "Training Minutes",
      days_completed: "Days Completed",
      videos_completed: "Videos Completed",
      leads: "Leads",
      first_meetings: "First Meetings",
      offers: "Offers",
      signed: "Signed",
    };
    return labels[metricType] || metricType || "";
  };

  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
  ];
  const gradient = gradients[(incentive.id || 0) % gradients.length];

  return (
    <div
      className={`incentive-grid-card${isSelected ? " incentive-grid-card--selected" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
    >
      {/* Banner image or gradient */}
      <div
        className="incentive-grid-card-banner"
        style={{ background: incentive.banner_url ? undefined : gradient }}
      >
        {incentive.banner_url && (
          <img
            src={incentive.banner_url}
            alt={incentive.title}
            className="incentive-grid-card-banner-img"
          />
        )}
        {/* Status badge overlay */}
        <span
          className="incentive-grid-card-status"
          style={{ background: getStatusColor(incentive.status) }}
        >
          {incentive.status}
        </span>
      </div>

      {/* Card body */}
      <div className="incentive-grid-card-body">
        <span className="incentive-grid-card-title">{incentive.title}</span>
        {incentive.metric_type && (
          <span className="incentive-grid-card-metric">
            {getMetricLabel(incentive.metric_type)}
          </span>
        )}
      </div>
    </div>
  );
}

// Incentive Detail View — shown when a card is clicked
function IncentiveDetailView({ incentive }) {
  const hasBenchmarkPrizes =
    incentive.prizes &&
    incentive.prizes.length > 0 &&
    incentive.prizes.some(p => p.benchmark_value != null);

  const isMilestoneType =
    incentive.type?.toUpperCase() === "MILESTONE" || hasBenchmarkPrizes;

  if (isMilestoneType) {
    return <MilestoneTimeline incentive={incentive} />;
  }

  return <IncentiveCard incentive={incentive} />;
}

// ── CompetitorPins ────────────────────────────────────────────────────────────
const PIN_SIZE = 34;
const PIN_OVERLAP = 10;
const MAX_VISIBLE = 6;

function CompetitorPin({ competitor, formatValue, style, className }) {
  const [hovered, setHovered] = useState(false);
  const initials =
    `${competitor.first_name?.charAt(0) || ""}${competitor.last_name?.charAt(0) || ""}`.toUpperCase();
  const label = `${competitor.first_name} ${competitor.last_name}: ${formatValue(competitor.current_value)}`;

  return (
    <div
      className={`cp-pin ${competitor.is_current_user ? "cp-pin--self" : ""} ${className || ""}`}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="cp-pin-inner">
        {competitor.profile_image ? (
          <img
            src={competitor.profile_image}
            alt={initials}
            className="cp-pin-img"
          />
        ) : (
          <span className="cp-pin-initials">{initials}</span>
        )}
      </div>
      {hovered && <div className="cp-pin-tooltip">{label}</div>}
    </div>
  );
}

function CompetitorOverflowBadge({ hiddenCompetitors, formatValue, style }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="cp-overflow"
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="cp-overflow-label">+{hiddenCompetitors.length}</span>
      {hovered && (
        <div className="cp-pin-tooltip cp-overflow-tooltip">
          {hiddenCompetitors
            .filter(c => c.first_name || c.last_name)
            .map((c, i) => (
              <div key={i} className="cp-overflow-name">
                {c.first_name} {c.last_name}: {formatValue(c.current_value)}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function CompetitorPins({ competitors, formatValue, prizeId }) {
  if (!competitors || competitors.length === 0) return null;

  const groups = [];
  const sorted = [...competitors].sort((a, b) => a.topPercent - b.topPercent);

  for (const item of sorted) {
    const last = groups[groups.length - 1];
    if (last && Math.abs(item.topPercent - last.topPercent) <= 3) {
      last.items.push(item);
      last.topPercent =
        last.items.reduce((s, x) => s + x.topPercent, 0) / last.items.length;
    } else {
      groups.push({ topPercent: item.topPercent, items: [item] });
    }
  }

  return (
    <>
      {groups.map((group, gi) => {
        const { topPercent, items } = group;

        const ordered = [...items].sort((a, b) => {
          if (a.competitor.is_current_user) return -1;
          if (b.competitor.is_current_user) return 1;
          return b.competitor.current_value - a.competitor.current_value;
        });

        const visible = ordered.slice(0, MAX_VISIBLE);
        const hidden = ordered.slice(MAX_VISIBLE);
        const hasOverflow = hidden.length > 0;

        const leftPins = [];
        const rightPins = [];

        visible.forEach((item, idx) => {
          if (idx % 2 === 0) {
            leftPins.push(item);
          } else {
            rightPins.push(item);
          }
        });

        const trackHalfWidth = 3;
        const gap = PIN_SIZE - PIN_OVERLAP;

        return (
          <React.Fragment key={`group-${prizeId}-${gi}`}>
            {/* Left side pins */}
            {leftPins.map((item, idx) => {
              const offset = trackHalfWidth + PIN_SIZE / 2 + idx * gap;
              return (
                <CompetitorPin
                  key={`pin-${item.competitor.user_season_id}-${prizeId}`}
                  competitor={item.competitor}
                  formatValue={formatValue}
                  style={{
                    position: "absolute",
                    top: `${topPercent}%`,
                    right: `calc(100% + ${offset - trackHalfWidth}px)`,
                    transform: "translateY(-50%)",
                    zIndex: 10 - idx,
                  }}
                />
              );
            })}

            {/* Right side pins */}
            {rightPins.map((item, idx) => {
              const offset = trackHalfWidth + PIN_SIZE / 2 + idx * gap;
              return (
                <CompetitorPin
                  key={`pin-${item.competitor.user_season_id}-${prizeId}`}
                  competitor={item.competitor}
                  formatValue={formatValue}
                  style={{
                    position: "absolute",
                    top: `${topPercent}%`,
                    left: `calc(100% + ${offset - trackHalfWidth}px)`,
                    transform: "translateY(-50%)",
                    zIndex: 10 - idx,
                  }}
                />
              );
            })}

            {/* Overflow badge — centered on the track so it doesn't push width */}
            {hasOverflow && (
              <CompetitorOverflowBadge
                key={`overflow-${prizeId}-${gi}`}
                hiddenCompetitors={hidden.map(h => h.competitor)}
                formatValue={formatValue}
                style={{
                  position: "absolute",
                  top: `${topPercent}%`,
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 15,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export default IncentivesTab;
